import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  Box,
  ListItemText,
  IconButton,
  Button,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Badge,
} from '@mui/material';

import { ReactNode, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

interface SideMenuItemProps {
  titleKey: string;
  path: string;
  iconPath: string;
  children?: JSX.Element | JSX.Element[];
  end?: boolean;
  hasBadge?: boolean;
  onClick?: () => void;
  additionalIcon?: ReactNode;
}

export const SideMenuItem = (props: SideMenuItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Box sx={{ mb: 2 }}>
      <ListItem
        key={props.titleKey}
        disablePadding
        disableGutters
        alignItems='flex-start'
      >
        <Box sx={{ width: '100%' }}>
          <NavLink
            end={props.end !== null ? props.end : !props.children}
            to={props.path}
            style={{ textDecoration: 'none' }}
            onClick={props.onClick}
          >
            {({ isActive }) => (
              <ListItemButton
                onClick={
                  props.children
                    ? () => setIsExpanded(!isExpanded)
                    : () => {
                        /* do nothing */
                      }
                }
                sx={{
                  pl: 0,
                }}
              >
                <Box
                  sx={{
                    width: 4,
                    height: 24,
                    flexGrow: 0,
                    borderTopRightRadius: 2,
                    borderBottomRightRadius: 2,
                    backgroundColor: isActive ? 'action.active' : 'transparent',
                  }}
                />
                <ListItemIcon
                  sx={{
                    width: 34,
                    minWidth: 0,
                    marginLeft: 3,
                  }}
                >
                  {props.children && (
                    <IconButton onClick={() => setIsExpanded(!isExpanded)}>
                      <img
                        width='24px'
                        height='24px'
                        src={
                          isExpanded
                            ? '/public/assets/icons/ic_collapse.svg'
                            : '/public/assets/icons/ic_expand.svg'
                        }
                      />
                    </IconButton>
                  )}
                </ListItemIcon>
                <Badge
                  invisible={!props.hasBadge}
                  variant='dot'
                  color='secondary'
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: '#d00070',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      pr: 2.5,
                      ...(isActive
                        ? {}
                        : {
                            '& img': {
                              filter:
                                'brightness(0) saturate(100%) invert(79%) sepia(8%) saturate(219%) hue-rotate(182deg) brightness(90%) contrast(92%)',
                            },
                          }),
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        width: 34,
                        minWidth: 0,
                      }}
                    >
                      <img
                        src={props.iconPath}
                        style={{
                          width: '24px',
                          height: '24px',
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={<FormattedMessage id={props.titleKey} />}
                      primaryTypographyProps={{
                        color: isActive ? '#fff' : '#d8dade',
                        fontWeight: isActive ? 600 : 400,
                      }}
                    />
                  </Box>
                </Badge>
                {props.additionalIcon}
              </ListItemButton>
            )}
          </NavLink>
          {isExpanded && (
            <Stepper
              orientation='vertical'
              connector={<div></div>}
              sx={{
                ml: 13,
                '& .MuiStepLabel-root': {
                  py: 0,
                },
              }}
            >
              {props.children}
            </Stepper>
          )}
        </Box>
      </ListItem>
    </Box>
  );
};

interface SideMenuChildProps {
  path: string;
  titleKey: string;
  isLast?: boolean;
  onChildClick?: () => void;
}

export const SideMenuItemChild = (props: SideMenuChildProps) => {
  return (
    <NavLink
      to={props.path}
      style={{ textDecoration: 'none' }}
      onClick={props.onChildClick}
    >
      {({ isActive }) => (
        <Step>
          <StepLabel
            StepIconComponent={props.isLast ? LastChildIcon : ChildIcon}
          >
            <Button
              sx={{
                color: isActive ? '#fff' : '#d8dade',
                fontWeight: isActive ? 600 : 400,
                fontSize: 13,
              }}
            >
              <FormattedMessage id={props.titleKey} />
            </Button>
          </StepLabel>
        </Step>
      )}
    </NavLink>
  );
};

export const ChildIcon = () => {
  return (
    <Stack alignItems='center' direction={'row'}>
      <div
        style={{
          width: '1px',
          height: '40px',
          background: '#898f9b',
        }}
      ></div>
      <div
        style={{
          width: '10px',
          height: '1px',
          background: '#898f9b',
        }}
      ></div>
    </Stack>
  );
};

export const LastChildIcon = () => {
  return (
    <Stack alignItems='center' direction={'row'}>
      <div
        style={{
          width: '1px',
          height: '20px',
          marginBottom: '20px',
          background: '#898f9b',
        }}
      ></div>

      <div
        style={{
          width: '10px',
          height: '1px',
          background: '#898f9b',
        }}
      ></div>
    </Stack>
  );
};
